// Page "Templates de relance" (Lot 4B admin) — liste à gauche, éditeur + preview
// à droite. Le user édite subject + body_text + body_html (optionnel) et peut
// prévisualiser le rendu avec les variables avant de sauvegarder.

import { FormEvent, useEffect, useMemo, useState } from "react";

import { ApiError } from "../api/client";
import { reminderTemplatesApi } from "../api/reminderTemplates";
import type {
  ReminderTemplatePreviewResponse,
  ReminderTemplatePublic,
} from "../api/types";
import { useTranslation } from "react-i18next";
import { AppShell } from "../components/AppShell";
import { FormField } from "../components/FormField";

// Ordre d'affichage voulu : amical → ferme → pré-contentieux, puis custom.
const TEMPLATE_ORDER: Record<string, number> = {
  reminder_friendly: 1,
  reminder_firm: 2,
  reminder_precontentieux: 3,
};

const TEMPLATE_LABEL: Record<string, string> = {
  reminder_friendly: "Amicale (J+7)",
  reminder_firm: "Ferme (J+14)",
  reminder_precontentieux: "Pré-contentieux (J+30)",
};

function labelFor(key: string): string {
  return TEMPLATE_LABEL[key] ?? key;
}

function sortTemplates(
  list: ReminderTemplatePublic[],
): ReminderTemplatePublic[] {
  return [...list].sort((a, b) => {
    const ra = TEMPLATE_ORDER[a.template_key] ?? 99;
    const rb = TEMPLATE_ORDER[b.template_key] ?? 99;
    if (ra !== rb) return ra - rb;
    return a.template_key.localeCompare(b.template_key);
  });
}

// Variables disponibles — doivent rester alignées avec _SAMPLE_VARIABLES côté
// backend (routes/reminder_templates.py). On les liste pour que le user sache
// ce qu'il peut insérer.
const AVAILABLE_VARIABLES: ReadonlyArray<{ key: string; hint: string }> = [
  { key: "client_name", hint: "Nom du client" },
  { key: "reference", hint: "Référence facture" },
  { key: "amount_ttc", hint: "Montant TTC formaté" },
  { key: "amount_ht", hint: "Montant HT formaté" },
  { key: "remaining", hint: "Restant dû TTC" },
  { key: "currency", hint: "Devise (EUR, AED, …)" },
  { key: "due_date", hint: "Échéance (JJ/MM/AAAA)" },
  { key: "issue_date", hint: "Date d'émission" },
  { key: "days_overdue", hint: "Jours de retard" },
  { key: "company_name", hint: "Nom du tenant émetteur" },
];

export function ReminderTemplatesPage() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<ReminderTemplatePublic[] | null>(
    null,
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const sorted = useMemo(
    () => (templates ? sortTemplates(templates) : []),
    [templates],
  );

  async function reload(keepKey?: string | null) {
    setListError(null);
    try {
      const data = await reminderTemplatesApi.list();
      setTemplates(data);
      // Préserver la sélection si toujours présente, sinon 1er template.
      const arr = sortTemplates(data);
      if (keepKey && arr.find((t) => t.template_key === keepKey)) {
        setSelectedKey(keepKey);
      } else if (arr.length > 0) {
        setSelectedKey(arr[0].template_key);
      } else {
        setSelectedKey(null);
      }
    } catch (err) {
      setListError(err instanceof Error ? err.message : "Erreur de chargement");
      setTemplates([]);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected =
    (selectedKey && sorted.find((t) => t.template_key === selectedKey)) ||
    null;

  return (
    <AppShell>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          {t("reminderTemplatesPage.title")}
        </h1>
        <p className="text-sm text-slate-500">
          {t("reminderTemplatesPage.subtitle")}</p>
      </header>

      {listError && (
        <p role="alert" className="mb-4 text-sm text-red-600">
          {listError}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6">
        <aside aria-label="Liste des templates">
          <ul className="space-y-1">
            {sorted.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setSelectedKey(t.template_key)}
                  className={[
                    "w-full text-left rounded-md px-3 py-2 text-sm",
                    t.template_key === selectedKey
                      ? "bg-brand-500/10 text-brand-700 font-medium"
                      : "text-slate-600 hover:bg-slate-100",
                  ].join(" ")}
                >
                  <div>{labelFor(t.template_key)}</div>
                  <div className="text-xs text-slate-400 truncate">
                    {t.template_key}
                  </div>
                </button>
              </li>
            ))}
            {sorted.length === 0 && templates !== null && (
              <li className="text-sm text-slate-500 p-3">
                {t("reminderTemplatesPage.noTemplates")}
              </li>
            )}
          </ul>
        </aside>

        <section aria-label="Éditeur">
          {templates === null && (
            <p className="text-sm text-slate-500">{t("common.loading")}</p>
          )}
          {selected && (
            <TemplateEditor
              key={selected.id}
              template={selected}
              onSaved={(key) => reload(key)}
            />
          )}
          {!selected && templates && templates.length === 0 && (
            <p className="text-sm text-slate-500">
              Aucun template à éditer.
            </p>
          )}
        </section>
      </div>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Éditeur d'un template
// ---------------------------------------------------------------------------

type EditorProps = {
  template: ReminderTemplatePublic;
  onSaved: (templateKey: string) => void;
};

function TemplateEditor({ template, onSaved }: EditorProps) {
  const { t } = useTranslation();
  const [subject, setSubject] = useState(template.subject);
  const [bodyText, setBodyText] = useState(template.body_text);
  const [bodyHtml, setBodyHtml] = useState(template.body_html ?? "");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [preview, setPreview] =
    useState<ReminderTemplatePreviewResponse | null>(null);
  const [previewPending, setPreviewPending] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Note : pas de useEffect [template] ici — le composant est remonté par
  // React quand le template sélectionné change (cf. `key={selected.id}` dans
  // le parent). Synchroniser l'état local sur la prop à chaque re-render
  // écraserait la saisie en cours (et ferait disparaître le flash "enregistré"
  // dès que reload() ramène la version fraîche).

  const dirty =
    subject !== template.subject ||
    bodyText !== template.body_text ||
    (bodyHtml || null) !== template.body_html;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setError(null);
    setSaved(false);
    try {
      await reminderTemplatesApi.save(template.template_key, {
        subject: subject.trim(),
        body_text: bodyText,
        body_html: bodyHtml.trim() ? bodyHtml : null,
      });
      setSaved(true);
      onSaved(template.template_key);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.detail : "Impossible d'enregistrer",
      );
    } finally {
      setPending(false);
    }
  }

  async function onPreview() {
    setPreviewPending(true);
    setPreviewError(null);
    try {
      const res = await reminderTemplatesApi.preview(template.template_key, {
        subject,
        body_text: bodyText,
        body_html: bodyHtml.trim() ? bodyHtml : null,
      });
      setPreview(res);
    } catch (err) {
      setPreviewError(
        err instanceof ApiError
          ? err.detail
          : "Impossible de générer le preview",
      );
    } finally {
      setPreviewPending(false);
    }
  }

  function insertVariable(key: string) {
    const placeholder = `{${key}}`;
    // On l'ajoute à la fin du body_text — pattern simple, assez pratique.
    setBodyText((prev) => (prev ? prev + " " + placeholder : placeholder));
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5" aria-label="template-form">
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {labelFor(template.template_key)}
          </h2>
          <p className="text-xs text-slate-400">
            {t("reminderTemplatesPage.key")} : <code>{template.template_key}</code>
          </p>
        </div>

        <FormField label={t("reminderTemplatesPage.emailSubject")} htmlFor="subject" required>
          <input
            id="subject"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="input"
            maxLength={255}
          />
        </FormField>

        <FormField
          label={t("reminderTemplatesPage.emailBody")}
          htmlFor="body_text"
          required
          hint="Version envoyée par défaut. Utilisez les variables listées à droite."
        >
          <textarea
            id="body_text"
            required
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            rows={12}
            className="input font-mono text-sm"
          />
        </FormField>

        <FormField
          label="Corps (HTML — optionnel)"
          htmlFor="body_html"
          hint="Laissez vide pour envoyer uniquement en texte."
        >
          <textarea
            id="body_html"
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            rows={6}
            className="input font-mono text-sm"
            placeholder="<p>Bonjour {client_name}…</p>"
          />
        </FormField>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        {saved && !dirty && (
          <p role="status" className="text-sm text-emerald-600">
            Template enregistré.
          </p>
        )}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={pending || !dirty}
            className="btn-primary"
          >
            {pending ? t("common.saving") : t("common.save")}
          </button>
          <button
            type="button"
            className="text-sm text-brand-600 hover:underline disabled:text-slate-400"
            onClick={onPreview}
            disabled={previewPending}
          >
            {previewPending ? "Preview…" : "Prévisualiser"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <aside
          aria-label="Variables disponibles"
          className="bg-white rounded-xl border border-slate-200 p-5"
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            Variables disponibles
          </h3>
          <p className="text-xs text-slate-500 mb-3">
            Cliquez pour insérer à la fin du corps texte.
          </p>
          <ul className="space-y-1">
            {AVAILABLE_VARIABLES.map((v) => (
              <li key={v.key}>
                <button
                  type="button"
                  className="text-left w-full rounded px-2 py-1 text-sm hover:bg-slate-50"
                  onClick={() => insertVariable(v.key)}
                >
                  <code className="text-brand-600">{`{${v.key}}`}</code>
                  <span className="text-xs text-slate-500 ml-2">
                    {v.hint}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section
          aria-label="Preview"
          className="bg-slate-50 rounded-xl border border-slate-200 p-5"
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            Preview
          </h3>
          {previewError && (
            <p role="alert" className="text-sm text-red-600">
              {previewError}
            </p>
          )}
          {!preview && !previewError && (
            <p className="text-xs text-slate-500">
              Cliquez sur « Prévisualiser » pour voir le rendu avec un jeu de
              données d'exemple.
            </p>
          )}
          {preview && (
            <div className="space-y-3">
              <div>
                <p className="text-xs uppercase text-slate-400">{t("reminderTemplatesPage.emailSubject")}</p>
                <p className="text-sm font-medium text-slate-900">
                  {preview.subject}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-400">{t("reminderTemplatesPage.emailBody")}</p>
                <pre className="whitespace-pre-wrap text-sm text-slate-800 bg-white rounded border border-slate-200 p-3">
                  {preview.body_text}
                </pre>
              </div>
              {preview.body_html && (
                <div>
                  <p className="text-xs uppercase text-slate-400">HTML</p>
                  <div
                    className="text-sm bg-white rounded border border-slate-200 p-3"
                    // Rendu HTML côté preview — source contrôlée par l'admin
                    // du tenant lui-même, pas d'injection externe.
                    dangerouslySetInnerHTML={{ __html: preview.body_html }}
                  />
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </form>
  );
}
